import React, { FC, useCallback, useEffect, useState } from "react";
import Button from "components/Button/Button";
import Modal from "components/Modal/Modal";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { ButtonColor, ButtonType, ButtonVariant } from "enums/Button";
import { setNestedObjectValues, useFormik } from "formik";
import { Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import useFetchOnceEffect from "../../../../hooks/useFetchOnceEffect";
import {
  cancelInboundLabel,
  clearInboundRateOptions,
  createInboundShipment,
  deleteInboundShipment,
  estimateInboundShipment,
  getInboundAddresses,
  getInboundShipment,
  shipInboundShipment,
  updateInboundShipment,
} from "../../../../reduxFolder/reducers/inbound";
import {
  closeRightPanel,
  openRightPanel,
} from "../../../../reduxFolder/reducers/rightPanel";
import { authSelectUnits } from "../../../../reduxFolder/selectors/globalSelectors";
import {
  inboundSelectAddresses,
  inboundSelectAddressesLoaded,
  inboundSelectAddressesLoading,
  inboundSelectCancelingLabel,
  inboundSelectDetailLoadingId,
  inboundSelectDetails,
  inboundSelectEstimating,
  inboundSelectRateOptions,
  inboundSelectShipping,
} from "../../../../reduxFolder/selectors/inboundSelectors";
import { OpenRightPanel } from "../../../../types/common/rightPanel";
import {
  deriveInboundStatus,
  getInboundEstimate,
  getInboundLabel,
} from "../../../../utils/inboundShipment";
import { toastResponseError } from "../../../../utils/responseMessageHelper";
import InboundLabelSection from "./InboundLabelSection";
import InboundQuoteSection from "./InboundQuoteSection";
import InboundShipmentForm from "./InboundShipmentForm";
import {
  buildInboundShipmentPayload,
  inboundShipmentFormik,
} from "./inboundShipmentFormHelper";
import {
  ButtonsWrapper,
  Container,
  ContentContainer,
  FormWrapper,
  Spinner,
} from "./InboundShipmentPanel.styles";

interface InboundShipmentPanelProps {
  panelData: any;
  details: any;
  detailLoadingId: number | null;
  addresses: any;
  isAddressesLoading: boolean;
  addressesLoaded: boolean;
  isEstimating: boolean;
  isShipping: boolean;
  isCancelingLabel: boolean;
  rateOptions: any;
  units: string;
  getInboundShipment: (id: number) => Promise<any>;
  createInboundShipment: (data: { inbound_shipment: any }) => Promise<any>;
  updateInboundShipment: (
    id: number,
    data: { inbound_shipment: any },
  ) => Promise<any>;
  deleteInboundShipment: (id: number) => Promise<any>;
  estimateInboundShipment: (id: number) => Promise<any>;
  shipInboundShipment: (id: number, rateId: string) => Promise<any>;
  cancelInboundLabel: (id: number) => Promise<any>;
  clearInboundRateOptions: (id: number) => void;
  getInboundAddresses: () => Promise<any>;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
}

const InboundShipmentPanel: FC<InboundShipmentPanelProps> = React.memo(
  ({
    panelData,
    details,
    detailLoadingId,
    addresses,
    isAddressesLoading,
    addressesLoaded,
    isEstimating,
    isShipping,
    isCancelingLabel,
    rateOptions,
    units,
    getInboundShipment,
    createInboundShipment,
    updateInboundShipment,
    deleteInboundShipment,
    estimateInboundShipment,
    shipInboundShipment,
    cancelInboundLabel,
    clearInboundRateOptions,
    getInboundAddresses,
    openRightPanel,
    closeRightPanel,
  }) => {
    const { t } = useTranslation("common");
    const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [isCancelLabelConfirmOpen, setCancelLabelConfirmOpen] =
      useState(false);
    const [selectedRateId, setSelectedRateId] = useState<string | null>(null);

    const id = panelData.get("id");
    const isEditForm = !!id;
    const shipment = isEditForm ? details.get(id) : undefined;
    const status = deriveInboundStatus(shipment);
    const isLabeled = status === "labeled";
    const isDetailLoading = detailLoadingId === id;
    const estimate = getInboundEstimate(shipment);
    const label = getInboundLabel(shipment);
    const shipmentRateOptions = isEditForm ? rateOptions.get(id) : undefined;

    useEffect(() => {
      if (id) {
        getInboundShipment(id).catch(toastResponseError);
      }
    }, [id, getInboundShipment]);

    // Fresh rates arrive cheapest-first — preselect the cheapest; a cleared
    // response (save/ship/failure) resets the selection.
    useEffect(() => {
      setSelectedRateId(
        shipmentRateOptions
          ? shipmentRateOptions.getIn(["rates", 0, "rate_id"], null)
          : null,
      );
    }, [shipmentRateOptions]);

    useFetchOnceEffect(!addressesLoaded, getInboundAddresses);

    const formProps = useFormik(
      inboundShipmentFormik(
        t,
        panelData,
        shipment,
        addresses,
        createInboundShipment,
        updateInboundShipment,
        getInboundShipment,
        openRightPanel,
      ),
    );
    const { handleSubmit, isSubmitting, dirty } = formProps;

    // The address form reopens this panel with preselectAddressId merged into
    // initialPanelData; other form fields reinitialize from the cached record.
    const handleAddAddress = useCallback(() => {
      openRightPanel(
        "INBOUND_ADDRESS_FORM",
        Map({
          returnPreviousPanel: "INBOUND_SHIPMENT",
          initialPanelData: panelData,
        }),
      );
    }, [openRightPanel, panelData]);

    // Refetch on estimate/ship/cancel failures: a 422 usually means the
    // server-side estimation state moved on (e.g. cleared by an edit), so the
    // cached detail must resync.
    const resyncDetail = useCallback(() => {
      if (id) {
        getInboundShipment(id).catch(() => undefined);
      }
    }, [id, getInboundShipment]);

    const handleGetQuote = useCallback(async () => {
      const errors = await formProps.validateForm();
      if (Object.keys(errors).length) {
        // Errors are nested (packages[i].weight) — flat setFieldTouched would
        // mark `packages` as a boolean and no field would highlight. This is
        // the same conversion Formik itself runs on a failed submit.
        formProps.setTouched(setNestedObjectValues(errors, true), false);
        return;
      }

      try {
        if (formProps.dirty) {
          await updateInboundShipment(
            id,
            buildInboundShipmentPayload(formProps.values),
          );
          await getInboundShipment(id);
        }
        await estimateInboundShipment(id);
      } catch (response) {
        toastResponseError(response);
        resyncDetail();
      }
    }, [
      formProps,
      id,
      updateInboundShipment,
      getInboundShipment,
      estimateInboundShipment,
      resyncDetail,
    ]);

    const handleBuyLabel = useCallback(() => {
      if (!selectedRateId) return;

      shipInboundShipment(id, selectedRateId)
        // The refetch brings the authoritative show label (total_charged,
        // per-package labels) in place of the transient ship response. Its
        // own failure is swallowed: the label is already bought and rendered
        // from the ship response, so it must not fall into the catch below
        // and read as a purchase failure.
        .then(() => getInboundShipment(id).catch(() => undefined))
        .catch((response: Error) => {
          toastResponseError(response);
          // A ship failure usually means the quoted rates expired or the
          // estimate is gone — drop them so the user re-quotes.
          clearInboundRateOptions(id);
          resyncDetail();
        });
    }, [
      id,
      selectedRateId,
      shipInboundShipment,
      getInboundShipment,
      clearInboundRateOptions,
      resyncDetail,
    ]);

    const handleCancelLabel = useCallback(() => {
      cancelInboundLabel(id)
        .then(() => getInboundShipment(id))
        .catch((response: Error) => {
          toastResponseError(response);
          resyncDetail();
        })
        .finally(() => setCancelLabelConfirmOpen(false));
    }, [id, cancelInboundLabel, getInboundShipment, resyncDetail]);

    const handleDelete = useCallback(() => {
      deleteInboundShipment(id)
        .then(() => {
          setDeleteConfirmOpen(false);
          closeRightPanel();
        })
        .catch((response: Error) => {
          toastResponseError(response);
          setDeleteConfirmOpen(false);
        });
    }, [id, deleteInboundShipment, closeRightPanel]);

    return (
      <ContentWrapper>
        <Heading>
          {t(isEditForm ? "inbound.shipmentTitle" : "inbound.newShipmentTitle")}
        </Heading>
        <ContentContainer>
          <Container>
            <Spinner isActive={isDetailLoading} />
            <FormWrapper onSubmit={handleSubmit}>
              <InboundShipmentForm
                {...formProps}
                addresses={addresses}
                isAddressesLoading={isAddressesLoading}
                units={units}
                isLabeled={isLabeled}
                isDetailLoading={isDetailLoading}
                onAddAddress={handleAddAddress}
              />
              {isEditForm && !isLabeled && (
                <InboundQuoteSection
                  estimate={estimate}
                  rateOptions={shipmentRateOptions}
                  selectedRateId={selectedRateId}
                  onSelectRate={setSelectedRateId}
                  isEstimating={isEstimating}
                  isShipping={isShipping}
                  isFormDirty={dirty}
                  disabled={isDetailLoading}
                  onGetQuote={handleGetQuote}
                  onBuyLabel={handleBuyLabel}
                />
              )}
              {isLabeled && label && (
                <InboundLabelSection
                  label={label}
                  packages={shipment ? shipment.get("packages") : undefined}
                  isCancelingLabel={isCancelingLabel}
                  onCancelLabel={() => setCancelLabelConfirmOpen(true)}
                />
              )}
              <ButtonsWrapper>
                {!isEditForm && (
                  <Button onClick={closeRightPanel}>
                    {t("common.cancel")}
                  </Button>
                )}
                {isEditForm && !isLabeled && (
                  <Button
                    color={ButtonColor.Danger}
                    onClick={() => setDeleteConfirmOpen(true)}
                  >
                    {t("common.remove")}
                  </Button>
                )}
                {/* Packages and sender are immutable once labeled — nothing
                    left to save. */}
                {!isLabeled && (
                  <Button
                    isLoading={isSubmitting}
                    disabled={isSubmitting || isDetailLoading}
                    type={ButtonType.Submit}
                    color={ButtonColor.Primary}
                    variant={ButtonVariant.Filled}
                  >
                    {t("common.saveChanges")}
                  </Button>
                )}
              </ButtonsWrapper>
            </FormWrapper>
          </Container>
        </ContentContainer>
        <Modal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          title={t("inbound.deleteConfirmTitle")}
          footer={
            <>
              <Button
                onClick={() => setDeleteConfirmOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant={ButtonVariant.Filled}
                color={ButtonColor.Danger}
                onClick={handleDelete}
              >
                {t("common.remove")}
              </Button>
            </>
          }
        >
          {t("inbound.deleteConfirmText")}
        </Modal>
        <Modal
          isOpen={isCancelLabelConfirmOpen}
          onClose={() => setCancelLabelConfirmOpen(false)}
          title={t("inbound.label.cancelConfirmTitle")}
          footer={
            <>
              <Button
                onClick={() => setCancelLabelConfirmOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant={ButtonVariant.Filled}
                color={ButtonColor.Danger}
                isLoading={isCancelingLabel}
                disabled={isCancelingLabel}
                onClick={handleCancelLabel}
              >
                {t("inbound.label.cancel")}
              </Button>
            </>
          }
        >
          {t("inbound.label.cancelConfirmText")}
        </Modal>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    details: inboundSelectDetails,
    detailLoadingId: inboundSelectDetailLoadingId,
    addresses: inboundSelectAddresses,
    isAddressesLoading: inboundSelectAddressesLoading,
    addressesLoaded: inboundSelectAddressesLoaded,
    isEstimating: inboundSelectEstimating,
    isShipping: inboundSelectShipping,
    isCancelingLabel: inboundSelectCancelingLabel,
    rateOptions: inboundSelectRateOptions,
    units: authSelectUnits,
  }),
  {
    getInboundShipment,
    createInboundShipment,
    updateInboundShipment,
    deleteInboundShipment,
    estimateInboundShipment,
    shipInboundShipment,
    cancelInboundLabel,
    clearInboundRateOptions,
    getInboundAddresses,
    openRightPanel,
    closeRightPanel,
  },
);

export default withConnect(InboundShipmentPanel);
